import { useState, useCallback, useMemo } from 'react';
import { chequeService } from '../services/cheques/chequeService';

let rowIdCounter = 0;
const generateRowId = () => `bulk-row-${++rowIdCounter}`;

export const useBulkChequeGrid = () => {
  const [stagingRows, setStagingRows] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const addRows = useCallback((rows) => {
    const newRows = rows.map(row => ({
      id: generateRowId(),
      chequeNumber: row.chequeNumber || '',
      beneficiaryName: row.beneficiaryName || '',
      chqAmount: row.chqAmount || 0,
      chqDate: row.chqDate || '',
      remarks: row.remarks || '',
      rowStatus: 'Staged',
      errorMessage: null,
      serverChequeId: null,
      source: row.source || 'manual'
    }));
    setStagingRows(prev => [...prev, ...newRows]);
  }, []);

  const addEmptyRow = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setStagingRows(prev => [...prev, {
      id: generateRowId(),
      chequeNumber: '',
      beneficiaryName: '',
      chqAmount: 0,
      chqDate: today,
      remarks: '',
      rowStatus: 'Staged',
      errorMessage: null,
      serverChequeId: null,
      source: 'manual'
    }]);
  }, []);

  const updateRow = useCallback((newRow) => {
    if (newRow.rowStatus !== 'Staged') return newRow;
    setStagingRows(prev => prev.map(row => row.id === newRow.id ? { ...newRow } : row));
    return newRow;
  }, []);

  const deleteRow = useCallback((id) => {
    setStagingRows(prev => prev.filter(row => row.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setStagingRows([]);
    setSelectedIds([]);
  }, []);

  const createAllCheques = useCallback(async (bankId) => {
    setIsCreating(true);
    // Track successfully-created cheques so callers can act on them (e.g. attach the
    // source voucher PDF) without racing the async stagingRows state update.
    const created = [];
    try {
      let stagedRows = stagingRows.filter(r => r.rowStatus === 'Staged');

      // ===== Pre-validation: catch duplicates BEFORE creating =====
      // Batch-check user-provided cheque numbers, swap any duplicates with the next
      // available number (incremented locally to avoid intra-batch collisions), and
      // update the grid so the user sees the corrected numbers before the create loop.
      const userProvided = stagedRows
        .map(r => ({ id: r.id, num: (r.chequeNumber || '').trim() }))
        .filter(x => x.num);
      const userProvidedNumbers = userProvided.map(x => x.num);

      if (userProvidedNumbers.length > 0) {
        try {
          const dupCheck = await chequeService.checkDuplicateChequeNumbers(bankId, userProvidedNumbers);
          const dupList = Array.isArray(dupCheck?.data) ? dupCheck.data : [];
          const dupSet = new Set(
            dupList.map(d => String(d.ChequeNumber ?? d.chequeNumber ?? '').trim())
          );

          if (dupSet.size > 0) {
            // Get next available number from backend, then increment locally per duplicate row.
            const nextResult = await chequeService.getNextChequeNumber(bankId);
            let nextCounter = parseInt(nextResult?.data?.nextChequeNumber, 10);
            const padLength = (nextResult?.data?.nextChequeNumber || '').length || 6;

            // Numbers that are NOT duplicates are already user-claimed for this batch.
            const claimed = new Set(
              userProvided
                .filter(x => !dupSet.has(x.num))
                .map(x => String(parseInt(x.num, 10)))
            );

            const replacements = {}; // rowId -> newChequeNumber
            for (const x of userProvided) {
              if (!dupSet.has(x.num)) continue;
              if (!Number.isFinite(nextCounter)) break;
              while (claimed.has(String(nextCounter))) nextCounter++;
              const padded = String(nextCounter).padStart(padLength, '0');
              replacements[x.id] = padded;
              claimed.add(String(nextCounter));
              nextCounter++;
            }

            const replacedIds = Object.keys(replacements);
            if (replacedIds.length > 0) {
              setStagingRows(prev => prev.map(r =>
                replacements[r.id] ? { ...r, chequeNumber: replacements[r.id] } : r
              ));
              // Reflect the swap in the local working copy so the create loop uses the new numbers.
              stagedRows = stagedRows.map(r =>
                replacements[r.id] ? { ...r, chequeNumber: replacements[r.id] } : r
              );
              console.log('[bulkCheque] pre-validation: replaced', replacedIds.length, 'duplicate(s)', replacements);
            }
          }
        } catch (preErr) {
          // Pre-validation is best-effort. If it fails, fall through and let the
          // per-row create loop and its post-error retry handle duplicates.
          console.warn('[bulkCheque] pre-validation failed, proceeding with create loop:', preErr);
        }
      }

      for (const row of stagedRows) {
        // Set status to Creating
        setStagingRows(prev => prev.map(r =>
          r.id === row.id ? { ...r, rowStatus: 'Creating' } : r
        ));

        try {
          const buildPayload = (chequeNumber) => {
            const p = {
              bankId,
              beneficiaryName: row.beneficiaryName,
              chqAmount: parseFloat(row.chqAmount),
              chqDate: row.chqDate,
              remarks: row.remarks || '',
            };
            if (chequeNumber) p.chequeNumber = chequeNumber;
            return p;
          };

          let assignedChequeNumber = row.chequeNumber;
          let result = await chequeService.createPrintedCheque(buildPayload(assignedChequeNumber));

          // If user-overridden number is a duplicate, auto-assign next available and retry once.
          const looksLikeDuplicate = !result.success && !!row.chequeNumber && (
            /already exists/i.test(result.message || '') ||
            /duplicate/i.test(result.message || '') ||
            result.errorCode === 'DUPLICATE_CHEQUE_NUMBER' ||
            result.status === 400
          );
          if (looksLikeDuplicate) {
            console.log('[bulkCheque] duplicate detected for', row.chequeNumber, '— fetching next available');
            const nextResult = await chequeService.getNextChequeNumber(bankId);
            const nextNumber = nextResult?.data?.nextChequeNumber;
            console.log('[bulkCheque] next-available =', nextNumber, 'success =', nextResult?.success);
            if (nextResult?.success && nextNumber) {
              assignedChequeNumber = nextNumber;
              result = await chequeService.createPrintedCheque(buildPayload(assignedChequeNumber));
              console.log('[bulkCheque] retry result for', nextNumber, '→', result.success ? 'OK' : `FAIL: ${result.message}`);
            }
          }

          if (result.success) {
            const chequeId = result.data?.Id || result.data?.id || result.data?.chequeId || result.data?.ChequeId;
            created.push({ rowId: row.id, serverChequeId: chequeId, source: row.source });
            setStagingRows(prev => prev.map(r =>
              r.id === row.id
                ? { ...r, chequeNumber: assignedChequeNumber || r.chequeNumber, rowStatus: 'Created', serverChequeId: chequeId, errorMessage: null }
                : r
            ));
          } else {
            setStagingRows(prev => prev.map(r =>
              r.id === row.id
                ? { ...r, rowStatus: 'Error', errorMessage: result.message || 'Creation failed' }
                : r
            ));
          }
        } catch (error) {
          setStagingRows(prev => prev.map(r =>
            r.id === row.id
              ? { ...r, rowStatus: 'Error', errorMessage: error.message || 'Unexpected error' }
              : r
          ));
        }

        // Small delay to prevent overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } finally {
      setIsCreating(false);
    }
    return created;
  }, [stagingRows]);

  const printAllCheques = useCallback(async (printConfig) => {
    setIsPrinting(true);
    try {
      const allCreated = stagingRows.filter(r => r.rowStatus === 'Created' && r.serverChequeId);

      // If user has ticked rows, print only those (mapping grid row id -> serverChequeId).
      // If nothing is ticked, fall back to printing all Created (preserves prior behavior).
      let target;
      if (selectedIds && selectedIds.length > 0) {
        const selectedSet = new Set(selectedIds);
        target = allCreated.filter(r => selectedSet.has(r.id)).map(r => r.serverChequeId);
      } else {
        target = allCreated.map(r => r.serverChequeId);
      }

      if (target.length === 0) return { success: false, message: 'No cheques to print' };

      const result = await chequeService.printBulkCheques(target, printConfig);
      return result;
    } finally {
      setIsPrinting(false);
    }
  }, [stagingRows, selectedIds]);

  const counts = useMemo(() => {
    const createdRows = stagingRows.filter(r => r.rowStatus === 'Created');
    const createdRowIdSet = new Set(createdRows.map(r => r.id));
    const validSelectedIds = (selectedIds || []).filter(id => createdRowIdSet.has(id));
    return {
      total: stagingRows.length,
      stagedCount: stagingRows.filter(r => r.rowStatus === 'Staged').length,
      creatingCount: stagingRows.filter(r => r.rowStatus === 'Creating').length,
      createdCount: createdRows.length,
      errorCount: stagingRows.filter(r => r.rowStatus === 'Error').length,
      selectedCount: validSelectedIds.length,
    };
  }, [stagingRows, selectedIds]);

  return {
    stagingRows,
    addRows,
    addEmptyRow,
    updateRow,
    deleteRow,
    clearAll,
    createAllCheques,
    printAllCheques,
    isCreating,
    isPrinting,
    selectedIds,
    setSelectedIds,
    ...counts,
  };
};

export default useBulkChequeGrid;
